// ============================================================
// FYUNOR — Locale-aware numeric & currency formatting
// Nothing is hardcoded; every formatter is driven by a locale
// descriptor so calculators stay region-agnostic.
// ============================================================

import { getCountry } from "@/lib/locale/countries";

const DEFAULT_LOCALE = {
  code: "en-IE",
  country: "IE",
  countryName: "Ireland",
  currency: "EUR",
  currencySymbol: "€",
  currencyPosition: "prefix", // "prefix" | "suffix"
  decimalSeparator: ".",
  thousandSeparator: ",",
  dateFormat: "DD/MM/YYYY",
  numberFormat: "1,000.00",
};

/**
 * Build a locale descriptor object. Future calculators pass this in;
 * the LocaleContext provides a default but never hardcodes values.
 */
export function buildLocale(overrides = {}) {
  return { ...DEFAULT_LOCALE, ...overrides };
}

/** Parse a user-typed numeric string into a Number (handles 1,000.00 and 1.000,00). */
export function parseNumberInput(raw, locale = DEFAULT_LOCALE) {
  if (raw == null) return null;
  const str = String(raw).trim();
  if (str === "") return null;
  // Strip currency symbols and spaces
  const cleaned = str.replace(/[^\d.,\-]/g, "");
  if (locale.decimalSeparator === ",") {
    // European: 1.000,00 → 1000.00
    const normalized = cleaned.replace(/\./g, "").replace(/,/g, ".");
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
  }
  const normalized = cleaned.replace(/,/g, "");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Format a number for display using Intl (respects thousands/decimal separators). */
export function formatNumber(value, { locale = DEFAULT_LOCALE, decimals = 2, notation = "standard" } = {}) {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(locale.code.replace("_", "-"), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    notation,
  }).format(value);
}

/** Compact formatting for large figures (e.g. 1.2M). */
export function formatCompact(value, { locale = DEFAULT_LOCALE, decimals = 1 } = {}) {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(locale.code.replace("_", "-"), {
    notation: "compact",
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format a value as currency using the locale's currency object. */
export function formatCurrency(value, { locale = DEFAULT_LOCALE, decimals } = {}) {
  if (value == null || Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat(locale.code.replace("_", "-"), {
      style: "currency",
      currency: locale.currency,
      minimumFractionDigits: decimals ?? (locale.currency === "JPY" ? 0 : 2),
      maximumFractionDigits: decimals ?? (locale.currency === "JPY" ? 0 : 2),
    }).format(value);
  } catch {
    const num = formatNumber(value, { locale, decimals: decimals ?? 2 });
    return locale.currencyPosition === "suffix"
      ? `${num}${locale.currencySymbol}`
      : `${locale.currencySymbol}${num}`;
  }
}

/** Format a decimal ratio (0.045) as a percentage (4.50%). */
export function formatPercent(ratio, { locale = DEFAULT_LOCALE, decimals = 2 } = {}) {
  if (ratio == null || Number.isNaN(ratio)) return "—";
  return new Intl.NumberFormat(locale.code.replace("_", "-"), {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(ratio);
}

/** The currency prefix shown inside an input field. */
export function currencyAffix(locale = DEFAULT_LOCALE) {
  return {
    symbol: locale.currencySymbol,
    position: locale.currencyPosition,
  };
}

/**
 * Build a locale descriptor from a country code (ISO-3166 alpha-2).
 * Used by calculator pages so currency/number/date formatting follows
 * the country in the URL rather than the app-wide context locale.
 * Falls back to the supplied fallback (or the default) when the
 * country is unknown.
 */
export function localeFromCountry(countryCode, fallback = DEFAULT_LOCALE) {
  const c = getCountry(countryCode);
  if (!c) return fallback;
  return buildLocale({
    code: c.locale,
    country: c.code,
    countryName: c.name,
    currency: c.currency,
    currencySymbol: c.currencySymbol,
    currencyPosition: c.currencyPosition,
    decimalSeparator: c.decimalSeparator,
    thousandSeparator: c.thousandsSeparator, // country uses plural; locale uses singular
    dateFormat: c.dateFormat,
  });
}